const SIDEBAR_ITEMS = [
  {
    key: 'chat',
    label: 'Chat',
    description: 'Primary conversational workspace for members.'
  },
  {
    key: 'documents',
    label: 'Documents',
    description: 'Upload, search, and manage knowledge documents.'
  },
  {
    key: 'queues',
    label: 'Queues',
    description: 'Workflow queues that coordinate automation and review.'
  },
  {
    key: 'graph',
    label: 'Knowledge Graph',
    description: 'Visualize relationships and connected knowledge.'
  },
  {
    key: 'tables',
    label: 'Tables',
    description: 'Structured datasets, queries, and collaborative analysis.'
  },
  {
    key: 'artifacts',
    label: 'Artifacts',
    description: 'Generated outputs, apps, and signed share links.'
  },
  {
    key: 'media',
    label: 'Media Studio',
    description: 'Generate, edit, and compose media with AI.'
  },
  {
    key: 'browser',
    label: 'Browser Automation',
    description: 'Automate web tasks with AI-powered browser workflows.'
  },
  {
    key: 'metrics',
    label: 'Metrics',
    description: 'High level usage metrics and engagement trends.'
  },
  {
    key: 'settings',
    label: 'Settings',
    description: 'Organization configuration, members, and billing.'
  },
  {
    key: 'help',
    label: 'Help & Guide',
    description: 'In-product documentation and onboarding tips.'
  }
];

const BASE_SIDEBAR_TEMPLATE = SIDEBAR_ITEMS.reduce((acc, item) => {
  acc[item.key] = true;
  return acc;
}, {});

// Default allowed models - all current models available to members
export const DEFAULT_MODEL_ALLOW_LIST = [
  // OpenAI GPT-5 Family (system keys available)
  'gpt-5.2',
  'gpt-5.1',
  'gpt-5',
  'gpt-5-pro',
  'gpt-5-mini',
  'gpt-5-nano',
  // OpenAI GPT-5.1 Codex (BYOK required)
  'gpt-5.1-codex',
  'gpt-5.1-codex-mini',
  'gpt-5.1-codex-max',
  
  // Anthropic Claude (BYOK required)
  'claude-opus-4-5-20251101',
  'claude-sonnet-4-5-20250929',
  'claude-haiku-4-5-20251001',
  
  // Google Gemini (BYOK required)
  'gemini-3-pro',
  'gemini-2.5-pro',
  'gemini-2.5-flash',
  'gemini-2.5-flash-lite',
  
  // xAI Grok (BYOK required)
  'grok-4-fast',
  'grok-4-fast-reasoning',
  'grok-4-fast-non-reasoning',
  'grok-4-1-fast-reasoning',
  'grok-4-1-fast-non-reasoning',
  'grok-4',

  // Private Models (Fireworks - system keys)
  'kimi-k2-instruct',
  'kimi-k2-instruct-thinking',
  'glm-4.6',
  'glm-4.5',
  'gpt-oss-120b',
  'qwen3-235b-a22b'
];

// Order of preference for default model selection
const MODEL_DEFAULT_PREFERENCE = [
  'gpt-5.2',
  'gpt-5.1',
  'gpt-5',
  'gpt-5-pro',
  'gpt-5-mini',
  'gpt-5-nano',
  'gpt-5.1-codex-max',
  'gemini-3-pro',
  'claude-opus-4-5-20251101',
  'claude-sonnet-4-5-20250929',
  'claude-haiku-4-5-20251001',
  'gemini-2.5-pro',
  'gemini-2.5-flash',
  'gemini-2.5-flash-lite',
  'grok-4-fast',
  'grok-4-fast-reasoning',
  'grok-4-fast-non-reasoning',
  'grok-4-1-fast-reasoning',
  'grok-4-1-fast-non-reasoning',
  'grok-4',
  'gpt-5.1-codex',
  'gpt-5.1-codex-mini',
  'qwen3-235b-a22b',
  'kimi-k2-instruct',
  'kimi-k2-instruct-thinking',
  'glm-4.6',
  'glm-4.5',
  'gpt-oss-120b'
];

const DEFAULT_MEMBER_PERMISSIONS = {
  owner: {
    version: 1,
    ui: {
      sidebar: { ...BASE_SIDEBAR_TEMPLATE }
    },
    models: {
      allowed: [...DEFAULT_MODEL_ALLOW_LIST],
      default: 'gpt-5.2'
    },
    tools: {
      allow: ['*']
    },
    capabilities: [
      {
        resource: '*',
        actions: ['*']
      }
    ]
  },
  admin: {
    version: 1,
    ui: {
      sidebar: { ...BASE_SIDEBAR_TEMPLATE }
    },
    models: {
      allowed: [...DEFAULT_MODEL_ALLOW_LIST],
      default: 'gpt-5.2'
    },
    tools: {
      allow: ['*']
    },
    capabilities: [
      {
        resource: '*',
        actions: ['read', 'write', 'update', 'delete', 'manage']
      }
    ]
  },
  member: {
    version: 1,
    ui: {
      sidebar: { ...BASE_SIDEBAR_TEMPLATE }
    },
    models: {
      allowed: [...DEFAULT_MODEL_ALLOW_LIST],
      default: 'gpt-5.2'
    },
    tools: {}
  },
  guest: {
    version: 1,
    ui: {
      sidebar: {
        ...BASE_SIDEBAR_TEMPLATE,
        tables: false,
        metrics: false,
        settings: false
      }
    },
    models: {
      allowed: [...DEFAULT_MODEL_ALLOW_LIST],
      default: 'gpt-5.2'
    },
    tools: {}
  }
};

// Model library for admin settings UI
const MODEL_LIBRARY = [
  {
    provider: 'OpenAI',
    providerKey: 'openai',
    requiresKey: false, // GPT-5 base models use system keys
    items: [
      { id: 'gpt-5.2', label: 'GPT-5.2', requiresKey: false },
      { id: 'gpt-5.1', label: 'GPT-5.1', requiresKey: false },
      { id: 'gpt-5', label: 'GPT-5', requiresKey: false },
      { id: 'gpt-5-pro', label: 'GPT-5 Pro', requiresKey: false },
      { id: 'gpt-5-mini', label: 'GPT-5 Mini', requiresKey: false },
      { id: 'gpt-5-nano', label: 'GPT-5 Nano', requiresKey: false },
      { id: 'gpt-5.1-codex', label: 'GPT-5.1 Codex (BYOK)', requiresKey: true },
      { id: 'gpt-5.1-codex-mini', label: 'GPT-5.1 Codex Mini (BYOK)', requiresKey: true },
      { id: 'gpt-5.1-codex-max', label: 'GPT-5.1 Codex Max (BYOK)', requiresKey: true }
    ]
  },
  {
    provider: 'Anthropic',
    providerKey: 'anthropic',
    requiresKey: true,
    items: [
      { id: 'claude-opus-4-5-20251101', label: 'Claude Opus 4.5 (200K)' },
      { id: 'claude-sonnet-4-5-20250929', label: 'Claude Sonnet 4.5 (1M beta)' },
      { id: 'claude-haiku-4-5-20251001', label: 'Claude Haiku 4.5 (200K, fast)' }
    ]
  },
  {
    provider: 'Google',
    providerKey: 'google',
    requiresKey: true,
    items: [
      { id: 'gemini-3-pro', label: 'Gemini 3 Pro' },
      { id: 'gemini-2.5-pro', label: 'Gemini 2.5 Pro' },
      { id: 'gemini-2.5-flash', label: 'Gemini 2.5 Flash' },
      { id: 'gemini-2.5-flash-lite', label: 'Gemini 2.5 Flash Lite' }
    ]
  },
  {
    provider: 'xAI',
    providerKey: 'xai',
    requiresKey: true,
    items: [
      { id: 'grok-4-fast', label: 'Grok 4 Fast' },
      { id: 'grok-4-fast-reasoning', label: 'Grok 4 Fast (Reasoning)' },
      { id: 'grok-4-fast-non-reasoning', label: 'Grok 4 Fast (Non-Reasoning)' },
      { id: 'grok-4-1-fast-reasoning', label: 'Grok 4.1 Fast (Reasoning)' },
      { id: 'grok-4-1-fast-non-reasoning', label: 'Grok 4.1 Fast (Non-Reasoning)' },
      { id: 'grok-4', label: 'Grok 4' }
    ]
  },
  {
    provider: 'Private',
    providerKey: 'private',
    requiresKey: false,
    items: [
      { id: 'qwen3-235b-a22b', label: 'Qwen3 235B' },
      { id: 'kimi-k2-instruct', label: 'Kimi K2 Instruct' },
      { id: 'kimi-k2-instruct-thinking', label: 'Kimi K2 Thinking' },
      { id: 'glm-4.6', label: 'GLM 4.6' },
      { id: 'glm-4.5', label: 'GLM 4.5' },
      { id: 'gpt-oss-120b', label: 'GPT-OSS 120B' }
    ]
  }
];

const ALL_MODEL_IDS = MODEL_LIBRARY.flatMap(group => group.items.map(item => item.id));

const MODEL_ALLOWED_SET = new Set([...DEFAULT_MODEL_ALLOW_LIST, ...ALL_MODEL_IDS]);

export const MEMBER_PERMISSION_METADATA = {
  version: 1,
  sections: [
    {
      id: 'ui.sidebar',
      label: 'Navigation Visibility',
      description: 'Toggle which navigation items appear for this member.',
      type: 'toggle-list',
      items: SIDEBAR_ITEMS
    },
    {
      id: 'models.allowed',
      label: 'Model Access',
      description: 'Choose which models appear in the chat model selector.',
      type: 'model-multi-select',
      items: MODEL_LIBRARY
    }
  ]
};

export function buildDefaultPermissions(role = 'member') {
  const normalizedRole = typeof role === 'string' ? role.toLowerCase() : 'member';
  const template = DEFAULT_MEMBER_PERMISSIONS[normalizedRole] || DEFAULT_MEMBER_PERMISSIONS.member;
  return JSON.parse(JSON.stringify(template));
}

export function normalizeMemberPermissions(rawPermissions, role = 'member') {
  if (!rawPermissions) {
    return buildDefaultPermissions(role);
  }

  const pickPreferredDefault = (allowed, current) => {
    if (current && allowed.includes(current)) {
      return current;
    }
    for (const preferred of MODEL_DEFAULT_PREFERENCE) {
      if (allowed.includes(preferred)) {
        return preferred;
      }
    }
    return allowed[0];
  };

  if (typeof rawPermissions === 'string') {
    try {
      const parsed = JSON.parse(rawPermissions);
      return normalizeMemberPermissions(parsed, role);
    } catch (error) {
      console.warn('[member-permissions] Failed to parse permissions string, falling back to defaults:', error);
      return buildDefaultPermissions(role);
    }
  }

  if (Array.isArray(rawPermissions)) {
    if (rawPermissions.includes('*')) {
      return buildDefaultPermissions(role);
    }
    const defaults = buildDefaultPermissions(role);
    const seen = new Set();
    const filtered = [];
    for (const modelId of rawPermissions) {
      if (typeof modelId !== 'string') continue;
      const trimmed = modelId.trim();
      if (!trimmed || seen.has(trimmed) || !MODEL_ALLOWED_SET.has(trimmed)) continue;
      seen.add(trimmed);
      filtered.push(trimmed);
    }
    const finalAllowed = filtered.length > 0 ? filtered : [...DEFAULT_MODEL_ALLOW_LIST];
    defaults.models.allowed = finalAllowed;
    defaults.models.default = pickPreferredDefault(finalAllowed, defaults.models.default);
    return defaults;
  }

  if (typeof rawPermissions === 'object') {
    const defaults = buildDefaultPermissions(role);
    const mergedSidebar = { ...defaults.ui.sidebar };
    const sidebarOverrides = rawPermissions.ui?.sidebar || {};
    Object.keys(sidebarOverrides).forEach((key) => {
      if (typeof sidebarOverrides[key] === 'boolean') {
        mergedSidebar[key] = sidebarOverrides[key];
      }
    });

    let allowedModels = [];
    if (Array.isArray(rawPermissions.models?.allowed)) {
      const seen = new Set();
      for (const modelId of rawPermissions.models.allowed) {
        if (typeof modelId !== 'string') continue;
        const trimmed = modelId.trim();
        if (!trimmed || seen.has(trimmed) || !MODEL_ALLOWED_SET.has(trimmed)) continue;
        seen.add(trimmed);
        allowedModels.push(trimmed);
      }
    }
    if (allowedModels.length === 0) {
      allowedModels = [...defaults.models.allowed];
    }

    // Respect admin's explicit default choice if valid
    const adminDefault = typeof rawPermissions.models?.default === 'string' 
      ? rawPermissions.models.default.trim() 
      : null;
    
    console.log('[member-permissions] normalizeMemberPermissions - adminDefault:', adminDefault, 'allowedModels:', allowedModels);
    
    const defaultModel = (adminDefault && allowedModels.includes(adminDefault))
      ? adminDefault
      : pickPreferredDefault(allowedModels, defaults.models.default);
    
    console.log('[member-permissions] normalizeMemberPermissions - final defaultModel:', defaultModel);

    return {
      version: 1,
      ui: {
        sidebar: mergedSidebar,
        panes: {
          ...(defaults.ui.panes || {}),
          ...(rawPermissions.ui?.panes || {})
        }
      },
      models: {
        allowed: allowedModels,
        default: defaultModel
      },
      tools: rawPermissions.tools || defaults.tools,
      capabilities: rawPermissions.capabilities || defaults.capabilities
    };
  }

  return buildDefaultPermissions(role);
}

export function hasSidebarAccess(permissions, key) {
  if (!permissions?.ui?.sidebar) {
    return true;
  }
  const value = permissions.ui.sidebar[key];
  return typeof value === 'boolean' ? value : true;
}

export function getAllowedModels(permissions) {
  if (permissions?.models?.allowed?.length) {
    const filtered = permissions.models.allowed
      .map(model => typeof model === 'string' ? model.trim() : '')
      .filter(model => model && MODEL_ALLOWED_SET.has(model));
    if (filtered.length) {
      return filtered;
    }
  }
  return [...DEFAULT_MODEL_ALLOW_LIST];
}

export function getModelMetadata() {
  return MODEL_LIBRARY.flatMap(group => group.items.map(item => ({
    ...item,
    provider: group.provider,
    providerKey: group.providerKey,
    requiresKey: typeof item.requiresKey === 'boolean' ? item.requiresKey : group.requiresKey
  })));
}
